import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FaqSection = () => {
    const faqData = [
        {
            question: "How do I apply for a personal loan?",
            answer: (
                <>
                    Applying is simple! Complete the online application process and you will get an instant intimation regarding your eligibility for a loan. You should keep following information ready:
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li>Basic details</li>
                        <li>KYC info like PAN, Aadhar Card</li>
                        <li>Last 6 months bank statement of salary account</li>
                        <li>Bank account details for getting loan amount and setup auto debit</li>
                    </ul>
                </>
            )
        },
        {
            question: "How quickly can I get the loan?",
            answer: "We offer fast loan processing with approvals typically within 24-48 hours. Once approved, funds are disbursed within 1-3 business days depending on your bank."
        },
        {
            question: "What are the eligibility criteria for a personal loan?",
            answer: (
                <>
                    Eligibility required is as mentioned below:
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li>You should be a salaried professional</li>
                        <li>You should have a PAN card and a bank account where salary gets deposited</li>
                        <li>You should have a good credit score, preferably &gt; 650. You can check here.</li>
                        <li>You should have a stable income, preferably &gt; INR 15,000.</li>
                        <li>Provide all the required documents and details during the application process.</li>
                        <li>We will validate all details and based on that you will get a credit limit assigned.</li>
                    </ul>
                </>
            )
        },
        {
            question: "What documents do I need to submit?",
            answer: (
                <>
                    Required documents are as mentioned below:
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li>Identity proof - PAN</li>
                        <li>Address proof - Aadhar, Voter card</li>
                        <li>Income proof - Last 6 months Bank statement</li>
                        <li>Bank account details</li>
                    </ul>
                    We have a process where we can get all these details online if you provide required consent and have the mobile number linked with your document proofs and bank accounts.
                </>
            )
        },
        {
            question: "What are the charges associated with the personal loan?",
            answer: (
                <>
                    <p style={{ marginBottom: 16 }}>
                        We work with multiple lending partners. Here are the key details about loan charges:
                    </p>
                    <ul style={{ marginTop: 8, marginBottom: 16, paddingLeft: 20 }}>
                        <li>You can apply for a loan from <strong>INR 2,500 to INR 5,00,000</strong> across India</li>
                        <li>Loan tenure can range from <strong>3 months to 18 months</strong></li>
                        <li>Applicable rate of interest can range from <strong>12% to 36%</strong> depending on your profile and selected lending partner for application</li>
                        <li>Annual Percentage Rate (APR) can range from <strong>18% to 100%</strong> based on the processing fee, account management fee or any other charge applied by lender based on your loan requirement</li>
                    </ul>
                    
                    <div style={{ 
                        background: '#f8f9fa', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        border: '1px solid #dee2e6',
                        marginTop: '16px'
                    }}>
                        <h6 style={{ marginBottom: '12px', color: '#495057', fontWeight: '600' }}>Sample Loan Example:</h6>
                        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                            <p><strong>Loan Amount:</strong> INR 10,000</p>
                            <p><strong>Loan Tenure:</strong> 90 days</p>
                            <p><strong>Interest Rate:</strong> 18.25% Annualised</p>
                            <p><strong>Account Management Fee:</strong> 54.75% Annualised</p>
                            <p><strong>APR (Interest Rate + Account Management Fee):</strong> 73% Annualised</p>
                            <p><strong>Interest Amount:</strong> INR 276</p>
                            <p><strong>Account Management Fee:</strong> INR 828</p>
                            <p><strong>GST on Account Management Fee:</strong> INR 149</p>
                            <p><strong>EMI:</strong> INR 3,418</p>
                        </div>
                    </div>
                </>
            )
        }
    ];

    const [activeIndex, setActiveIndex] = useState(faqData.length - 1); // Last item expanded by default

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? -1 : index);
    };

    // Staggered entrance animation
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.5,
                delayChildren: 0.5
            }
        }
    };

    const itemVariants = {
        hidden: { 
            opacity: 0, 
            y: 30,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        },
        hover: {
            y: -3,
            boxShadow: "0 10px 40px rgba(14, 41, 82, 0.4)",
            transition: {
                duration: 0.0,
                ease: "easeInOut"
            }
        }
    };

    // Answer expansion animation
    const answerVariants = {
        hidden: {
            opacity: 0,
            height: 0,
            transition: {
                duration: 0.1,
                ease: "easeInOut"
            }
        },
        visible: {
            opacity: 1,
            height: "auto",
            transition: {
                duration: 0.2,
                ease: "easeInOut"
            }
        }
    };

    return (
        <section className="homepage-faq-section mt-5 mb-4">
            <div className="homepage-faq-container">
                <motion.h2 
                    className="homepage-faq-title"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    viewport={{ once: true }}
                >
                    We're Here to Answer All Your Questions
                </motion.h2>
                
                <motion.div 
                    className="homepage-faq-accordion"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    {faqData.map((faq, index) => (
                        <motion.div 
                            key={index} 
                            className={`homepage-faq-item ${activeIndex === index ? 'homepage-faq-item-active' : ''}`}
                            variants={itemVariants}
                            whileHover="hover"
                            onClick={() => toggleAccordion(index)}
                        >
                            <div className="homepage-faq-question">
                                <span>{faq.question}</span>
                                <div className="homepage-faq-icon">
                                    {activeIndex === index ? (
                                        <span className="homepage-faq-minus-icon">−</span>
                                    ) : (
                                        <span className="homepage-faq-plus-icon">+</span>
                                    )}
                                </div>
                            </div>
                            <AnimatePresence>
                                {activeIndex === index && (
                                    <motion.div 
                                        className="homepage-faq-answer"
                                        variants={answerVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="hidden"
                                    >
                                        {faq.answer}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default FaqSection;
